import React, { useState, useEffect } from "react";
import { 
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { DASHBOARD_TYPE, RETRY_TIME } from "src/constant/common";
import { useQuery } from "src/Hooks/useReactQuery";
import { get6DofDashboardLink } from "src/services/simulator-flight";
import { useSetRecoilState } from "recoil";
import { alertState } from "src/recoil/atoms/global";
import { DASHBOARD_STATUS } from "src/constant/common";
import TooltipWrapper from "../TooltipWrapper";

const SimulationMCSDashboard = ({simulationData, isShow = true }) => {
  const { t } = useTranslation("flight-simulator");
  const showAlert = useSetRecoilState(alertState);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [dashboardType, setDashboardType] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const { data, refetch, isFetching, isError, error } = useQuery(
    [`getMCSDashboardUrl`, simulationData?.id, dashboardType],
    () =>
      get6DofDashboardLink({
        id: simulationData.id,
        dashboard_input_type: 'multiple',
        dashboard_type: dashboardType
      }),
    {
      enabled: false,
      retry: false,
    }
  );

  const getDashboardUrl = (data, dashboardType) => {
    const dashboard = data?.simulator_dashboards.find(
      d => d.dashboard_type === dashboardType
    );
    return dashboard ? dashboard.dashboard_url : null;
  }

  const isFullDashboard = getDashboardUrl(simulationData, DASHBOARD_TYPE.MCS_TRIMMING) && 
    getDashboardUrl(simulationData, DASHBOARD_TYPE.MCS_STATISTICAL)

  const handleGetDashboardUrl = (type) => {
    if (!simulationData) return;
    const oldDashboardUrl = getDashboardUrl(simulationData, type);
    if (simulationData.multi_dashboard_status === DASHBOARD_STATUS.SUCCEEDED && oldDashboardUrl) {
      window.open(oldDashboardUrl, "_blank");
      return;
    }
    setDashboardType(type);
    setIsChecking(true);
    setRetryCount(0);
  }

  useEffect(() => {
    if (dashboardType && isChecking) {
      refetch();
    }
  }, [dashboardType]);

  useEffect(() => {
    if (!data || isFetching || !dashboardType || !isChecking) return;
  
    if (data?.data?.dashboard_status === DASHBOARD_STATUS.SUCCEEDED) {
      setIsChecking(false);
      window.open(data.data.dashboard_url, "_blank");
      return;
    }
  
    if (retryCount >= RETRY_TIME) {
      showAlert({
        status: true,
        message: t("over_time"),
        type: "danger",
      });
      setIsChecking(false);
      return;
    }
  
    const timer = setTimeout(() => {
      setRetryCount((prev) => prev + 1);
      refetch();
    }, 3000);
  
    return () => clearTimeout(timer);
  }, [data, retryCount, refetch, isFetching, isChecking, dashboardType]);
  
  useEffect(() => {
    if (isError && error && isChecking) {
      setIsChecking(false);
      showAlert({
        status: true,
        message: error?.response?.data?.error?.detail || error?.message,
        type: "danger",
      });
    }
  }, [isError, error, isChecking]);

  return (
    isShow ? (
      isFetching ? (
        <Spinner size="sm" color="secondary" />
      ) : (
        <TooltipWrapper
          id={`dashboard-${simulationData?.id}`}
          tooltipText={t("list.table.dashboard")}
        >
          <Dropdown
            isOpen={dropdownOpen}
            toggle={toggleDropdown}
            className="align-self-center"
          >
            <DropdownToggle
              data-toggle="dropdown"
              tag="button"
              className="border-0 bg-transparent"
            >
              <i className={`bi bi-speedometer2 ${isFullDashboard ? 'text-success' : 'text-dark'} f-s-20`}/>
            </DropdownToggle>
            
            <DropdownMenu
              end={true}
              className="topbar-dropdown-menu profile-dropdown-items"
            >
              <div onClick={toggleDropdown}>
                <div
                  role="button"
                  className={`dropdown-item ${getDashboardUrl(simulationData, DASHBOARD_TYPE.MCS_TRIMMING) ? 'text-success' : 'text-dark'}`}
                  onClick={() => handleGetDashboardUrl(DASHBOARD_TYPE.MCS_TRIMMING)}
                >
                  <span>{t("list.table.msc_trimming_multi_dashboard")}</span>
                </div>
                <div
                  role="button"
                  className={`dropdown-item ${getDashboardUrl(simulationData, DASHBOARD_TYPE.MCS_STATISTICAL) ? 'text-success' : 'text-dark'}`}
                  onClick={() => handleGetDashboardUrl(DASHBOARD_TYPE.MCS_STATISTICAL)}
                >
                  <span>{t("list.table.msc_statistical_multi_dashboard")}</span>
                </div>
              </div>
            </DropdownMenu>
          </Dropdown>
        </TooltipWrapper>
      )
    ) : null
  );
};

export default SimulationMCSDashboard;
